import { decodeBase32, InvalidTotpSecretError, TotpAlgorithm } from 'vault-crypto';

export interface TotpParameters {
  secret: string;
  algorithm?: TotpAlgorithm;
  digits?: number;
  period?: number;
}

export default function extractNormalizedTotpSecret(
  totpSecretCandidate: string,
): string | undefined {
  const secret = totpSecretCandidate.toUpperCase().replace(/\s/g, '');
  try {
    // Try to decode b32, if this throws we're not dealing with a valid totp secret
    decodeBase32(secret);
    return secret;
  } catch (e) {
    if (e instanceof InvalidTotpSecretError) {
      return undefined;
    }
  }
  return undefined;
}

export function validateTotp(totpSecretOrOtpauthUrlCandidate: string) {
  const parameters = extractTotpParametersFromOtpauthUrl(totpSecretOrOtpauthUrlCandidate);
  if (parameters) {
    return true;
  }
  return extractNormalizedTotpSecret(totpSecretOrOtpauthUrlCandidate) !== undefined;
}

export function extractTotpParametersFromOtpauthUrl(
  otpauthUrlCandidate: string,
): TotpParameters | undefined {
  // otpauth://totp/?secret=qw&algorithm=SHA1&digits=8&period=30&lock=false
  let url;
  try {
    url = new URL(otpauthUrlCandidate);
  } catch {
    return undefined;
  }
  if (url.protocol !== 'otpauth:' && url.protocol !== 'apple-otpauth:') {
    return undefined;
  }
  const type = url.host;
  if (type.toLowerCase() !== 'totp') {
    return undefined;
  }

  const urlParams = new URLSearchParams(url.search);

  const secret = urlParams.get('secret');
  if (secret === null) {
    return undefined;
  }

  const normalizedSecret = extractNormalizedTotpSecret(secret);
  if (!normalizedSecret) {
    return undefined;
  }

  const params = {
    secret: normalizedSecret,
    algorithm: normalizeTotpAlgorithm(urlParams.get('algorithm')),
    digits: normalizeTotpDigits(urlParams.get('digits')),
    period: normalizeTotpPeriod(urlParams.get('period')),
  };
  return params;
}

function normalizeTotpAlgorithm(algorithm: string | null): TotpAlgorithm | undefined {
  if (algorithm === null) {
    return undefined;
  }
  const algo = algorithm.toLowerCase().trim();
  switch (algo) {
    case 'sha1':
      return TotpAlgorithm.sha1;
    case 'sha256':
      return TotpAlgorithm.sha256;
    case 'sha512':
      return TotpAlgorithm.sha512;
    default:
      // If we don't know the algo at parse time return undefined. We'll teat that as SHA1 later on.
      return undefined;
  }
}

function normalizeTotpDigits(digitsString: string | null): number | undefined {
  if (digitsString === null) {
    return undefined;
  }
  const digits = Number.parseInt(digitsString);
  if (Number.isNaN(digits) || !Number.isInteger(digits)) {
    return undefined;
  }
  if (digits <= 0) {
    return undefined;
  }
  if (digits > 10) {
    return undefined;
  }
  return digits;
}

function normalizeTotpPeriod(periodString: string | null): number | undefined {
  if (periodString === null) {
    return undefined;
  }
  const period = Number.parseInt(periodString);
  if (Number.isNaN(period) || !Number.isInteger(period)) {
    return undefined;
  }
  if (period <= 0) {
    return undefined;
  }
  return period;
}
